/**
 * Draws a level: the blocks somebody placed, as floor, wall and doorway.
 *
 * Everything the view needs beyond the placements themselves is **derived**, by
 * `resolveLevel` in shared and nowhere else -- which blocks are where, which ports
 * met, and which were left to seal. §4.2 rule 3 is the whole of the wall rule: a port
 * with nothing across it renders as wall, so this draws a wall along every footprint
 * boundary edge and replaces it with a doorway only where a `Connection` says two
 * ports mated.
 *
 * Geometry and legality come from shared; only the look is the client's. The editor's
 * `planPainter.ts` answers the same question for the plan view and is deliberately not
 * imported: `admin-editor` is an app rather than a shared package, and a view of a
 * dungeon at eye level is not a view of it from above.
 *
 * It also draws the **block in hand** over that board for the Build screen -- the one
 * thing here that is not the dungeon itself. It belongs to the view rather than to the
 * screen because a ghost is the same geometry resolved the same way, and a second place
 * that turned a block into cells would be a second place that could turn it differently.
 *
 * The real POLYGON art arrives at stage 4 and replaces the boxes without the rules
 * above changing.
 */
import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import {
  SIDES,
  TILE_SIZE,
  edgeKey,
  portCells,
  portEdge,
  resolveLevel,
  resolvePlacement,
  sideStep,
  tileToWorld,
  translateCell,
  type BlockCatalogue,
  type BlockRole,
  type Cell,
  type DungeonLevel,
  type PlacedBlock,
  type ResolvedLevel,
  type Side,
} from '@dungeon-builder/shared';

const FLOOR_HEIGHT = 0.25;
const WALL_HEIGHT = 3.5;
const WALL_THICKNESS = 0.4;
/** A port is 2.5 m of the 5 m edge, centred -- §4.2's one opening size. */
const PORT_WIDTH = 2.5;
/** What is left of the edge either side of that opening: the two jambs of a doorway. */
const JAMB_LENGTH = (TILE_SIZE - PORT_WIDTH) / 2;

/**
 * Placeholder floor per block role, so a room reads as a room and the way out reads
 * as the way out before any art exists.
 */
const ROLE_COLOUR: Record<BlockRole, Color3> = {
  link: new Color3(0.2, 0.19, 0.24),
  room: new Color3(0.29, 0.26, 0.33),
  goal: new Color3(0.34, 0.28, 0.15),
  threat: new Color3(0.33, 0.18, 0.21),
};

const WALL_COLOUR = new Color3(0.38, 0.35, 0.42);

/** The block in hand: green where the three rules accept it, red where they refuse. */
const GHOST_VALID = new Color3(0.33, 0.74, 0.45);
const GHOST_INVALID = new Color3(0.82, 0.33, 0.36);
const GHOST_ALPHA = 0.5;
/** Thin enough to read as a wash over the floor rather than as another slab. */
const GHOST_HEIGHT = 0.12;

function cellKey(cell: Cell): string {
  return `${cell.x}:${cell.z}`;
}

/** The world-space centre of the boundary a cell's named side opens through. */
function edgeCentre(cell: Cell, side: Side): Vector3 {
  const world = tileToWorld(cell.x, cell.z);
  const step = sideStep(side);
  return new Vector3(world.x + (step.x * TILE_SIZE) / 2, 0, world.z + (step.z * TILE_SIZE) / 2);
}

/** Renders a `DungeonLevel` as untextured blocks. Disposable and rebuildable. */
export class DungeonView {
  private readonly meshes: Mesh[] = [];
  /** The block in hand, kept apart so a cursor moving never redraws the dungeon. */
  private readonly ghostMeshes: Mesh[] = [];
  private readonly materials = new Map<string, StandardMaterial>();

  constructor(private readonly scene: Scene) {}

  /**
   * Draw the level, and hand back the board it was drawn from so a caller that needs
   * the same answers -- where the entrance is, what connects to what -- reads them
   * rather than resolving the level a second time.
   */
  load(level: DungeonLevel, catalogue: BlockCatalogue): ResolvedLevel {
    this.clear();
    const resolved = resolveLevel(level, catalogue);

    // Rule 3 in one lookup: an edge a connection carries is a way through, and every
    // other boundary edge is wall.
    const open = new Set(resolved.connections.flatMap((connection) => connection.edges).map(edgeKey));
    // Two blocks standing back to back share a boundary edge, so whichever reaches it
    // first draws it and the second leaves it alone.
    const drawn = new Set<string>();

    for (const block of resolved.blocks) {
      const colour = ROLE_COLOUR[block.definition.role];
      const inside = new Set(block.cells.map(cellKey));

      for (const cell of block.cells) this.floor(cell, block.definition.role, colour);

      for (const cell of block.cells) {
        for (const side of SIDES) {
          if (inside.has(cellKey(translateCell(cell, sideStep(side))))) continue;
          const key = edgeKey(portEdge(cell, side));
          if (drawn.has(key)) continue;
          drawn.add(key);
          if (open.has(key)) this.doorway(key, cell, side);
          else this.wall(key, cell, side);
        }
      }
    }

    return resolved;
  }

  /**
   * The block in hand, over the board: its cells washed green where the rules accept
   * it and red where they refuse, and a bar across every port so which way it faces is
   * visible before it is dropped -- which is the whole point of rotating and mirroring
   * it. A block the catalogue does not hold draws nothing rather than a guess.
   */
  ghost(placed: PlacedBlock, catalogue: BlockCatalogue, accepted: boolean): void {
    this.clearGhost();
    const resolved = resolvePlacement(placed, catalogue);
    if (!resolved) return;
    const name = accepted ? 'ghost-valid' : 'ghost-invalid';
    const colour = accepted ? GHOST_VALID : GHOST_INVALID;

    for (const cell of resolved.cells) {
      const world = tileToWorld(cell.x, cell.z);
      this.ghostBox(
        `ghost_floor_${cell.x}_${cell.z}`,
        { width: TILE_SIZE * 0.9, depth: TILE_SIZE * 0.9, height: GHOST_HEIGHT },
        new Vector3(world.x, FLOOR_HEIGHT + GHOST_HEIGHT / 2, world.z),
        name,
        colour,
      );
    }

    for (const port of resolved.ports) {
      for (const cell of portCells(port)) {
        const centre = edgeCentre(cell, port.side);
        const along = port.side === 'north' || port.side === 'south';
        this.ghostBox(
          `ghost_port_${cell.x}_${cell.z}_${port.side}`,
          {
            width: along ? PORT_WIDTH : WALL_THICKNESS,
            depth: along ? WALL_THICKNESS : PORT_WIDTH,
            height: WALL_HEIGHT / 2,
          },
          new Vector3(centre.x, FLOOR_HEIGHT + WALL_HEIGHT / 4, centre.z),
          name,
          colour,
        );
      }
    }
  }

  clearGhost(): void {
    for (const mesh of this.ghostMeshes) mesh.dispose();
    this.ghostMeshes.length = 0;
  }

  clear(): void {
    for (const mesh of this.meshes) mesh.dispose();
    this.meshes.length = 0;
  }

  dispose(): void {
    this.clear();
    this.clearGhost();
    for (const material of this.materials.values()) material.dispose();
    this.materials.clear();
  }

  /** One cell of floor, coloured by what its block is for. */
  private floor(cell: Cell, role: BlockRole, colour: Color3): void {
    const world = tileToWorld(cell.x, cell.z);
    this.box(
      `floor_${cell.x}_${cell.z}`,
      { width: TILE_SIZE * 0.98, depth: TILE_SIZE * 0.98, height: FLOOR_HEIGHT },
      new Vector3(world.x, FLOOR_HEIGHT / 2, world.z),
      role,
      colour,
    );
  }

  /** A boundary edge with nothing mated across it: wall, the whole 5 m of it. */
  private wall(key: string, cell: Cell, side: Side): void {
    const centre = edgeCentre(cell, side);
    const along = side === 'north' || side === 'south';
    this.box(
      `wall_${key}`,
      {
        width: along ? TILE_SIZE : WALL_THICKNESS,
        depth: along ? WALL_THICKNESS : TILE_SIZE,
        height: WALL_HEIGHT,
      },
      new Vector3(centre.x, WALL_HEIGHT / 2, centre.z),
      'wall',
      WALL_COLOUR,
    );
  }

  /**
   * A mated port: the two jambs either side of the opening, and open floor between
   * them. Drawn as jambs rather than as a missing wall so a doorway reads as one --
   * the opening is 2.5 m of the 5 m edge, not the whole of it.
   */
  private doorway(key: string, cell: Cell, side: Side): void {
    const centre = edgeCentre(cell, side);
    const along = side === 'north' || side === 'south';
    const offset = (TILE_SIZE - JAMB_LENGTH) / 2;
    const size = {
      width: along ? JAMB_LENGTH : WALL_THICKNESS,
      depth: along ? WALL_THICKNESS : JAMB_LENGTH,
      height: WALL_HEIGHT,
    };
    for (const [index, sign] of [-1, 1].entries()) {
      const position = along
        ? new Vector3(centre.x + sign * offset, WALL_HEIGHT / 2, centre.z)
        : new Vector3(centre.x, WALL_HEIGHT / 2, centre.z + sign * offset);
      this.box(`jamb_${key}_${index}`, size, position, 'wall', WALL_COLOUR);
    }
  }

  private box(
    name: string,
    size: { width: number; depth: number; height: number },
    position: Vector3,
    material: string,
    colour: Color3,
  ): void {
    this.meshes.push(this.make(name, size, position, material, colour));
  }

  private ghostBox(
    name: string,
    size: { width: number; depth: number; height: number },
    position: Vector3,
    material: string,
    colour: Color3,
  ): void {
    this.ghostMeshes.push(this.make(name, size, position, material, colour));
  }

  /**
   * One box. Nothing the view draws answers a pointer pick: the Build screen picks
   * against its own ground plane, and a 3.5 m wall standing between the cursor and
   * that plane would otherwise swallow every click aimed at the cell behind it.
   */
  private make(
    name: string,
    size: { width: number; depth: number; height: number },
    position: Vector3,
    material: string,
    colour: Color3,
  ): Mesh {
    const mesh = MeshBuilder.CreateBox(name, size, this.scene);
    mesh.position = position;
    mesh.material = this.materialFor(material, colour);
    mesh.isPickable = false;
    return mesh;
  }

  private materialFor(name: string, colour: Color3): StandardMaterial {
    let material = this.materials.get(name);
    if (!material) {
      material = new StandardMaterial(`mat_${name}`, this.scene);
      material.diffuseColor = colour;
      material.specularColor = new Color3(0.06, 0.06, 0.08);
      // The two ghost materials are the only translucent ones: a block in hand has
      // not been placed yet, and the floor it is over has to stay readable under it.
      if (name.startsWith('ghost-')) material.alpha = GHOST_ALPHA;
      this.materials.set(name, material);
    }
    return material;
  }
}
